import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ContainerHorizontalProps {
    children: React.ReactNode;
    className?: string;
    dir?: 'ltr' | 'rtl';
}

const FADE = 48;

/**
 * Horizontal scroller: native swipe/scroll, edges fade where more content
 * hides, and arrows jump to the next hidden item. Arrows show on hover for
 * mouse users and stay visible on touch screens.
 */
const ContainerHorizontal: React.FC<ContainerHorizontalProps> = ({
    children,
    className = '',
    dir = 'ltr',
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [canStart, setCanStart] = useState(false);
    const [canEnd, setCanEnd] = useState(false);

    const isRtl = useCallback(() => {
        const el = containerRef.current;
        return el ? getComputedStyle(el).direction === 'rtl' : dir === 'rtl';
    }, [dir]);

    const update = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        // Compare item edges with the viewport instead of scrollLeft: snap
        // points and container padding can leave scrollLeft a few px off zero
        // while nothing is actually hidden.
        const view = el.getBoundingClientRect();
        const items = Array.from(el.children).map(child => child.getBoundingClientRect());
        const leftHidden = items.some(r => r.width > 0 && r.left < view.left - 2);
        const rightHidden = items.some(r => r.width > 0 && r.right > view.right + 2);
        const rtl = isRtl();
        setCanStart(rtl ? rightHidden : leftHidden);
        setCanEnd(rtl ? leftHidden : rightHidden);
    }, [isRtl]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(update);
        ro.observe(el);
        Array.from(el.children).forEach(child => ro.observe(child));
        el.addEventListener('scroll', update, { passive: true });
        update();
        return () => { ro.disconnect(); el.removeEventListener('scroll', update); };
    }, [update, children]);

    // Scroll so the first item hidden on that side comes fully into view.
    const scrollToItem = (forward: boolean) => {
        const box = containerRef.current;
        if (!box) return;
        const view = box.getBoundingClientRect();
        const items = Array.from(box.children) as HTMLElement[];
        const toRight = isRtl() !== forward;
        const target = toRight
            ? items.find(el => el.getBoundingClientRect().right > view.right + 1)
            : [...items].reverse().find(el => el.getBoundingClientRect().left < view.left - 1);
        let delta = (toRight ? 1 : -1) * box.clientWidth * 0.6;
        if (target) {
            const r = target.getBoundingClientRect();
            delta = toRight ? r.right - view.right + FADE : r.left - view.left - FADE;
        }
        box.scrollBy({ left: delta, behavior: 'smooth' });
    };

    // "start" is left in LTR and right in RTL; the mask is drawn in screen space.
    const rtl = dir === 'rtl';
    const leftHidden = rtl ? canEnd : canStart;
    const rightHidden = rtl ? canStart : canEnd;
    const mask = !leftHidden && !rightHidden ? undefined
        : `linear-gradient(to right, ${leftHidden ? 'transparent' : 'black'} 0, black ${FADE}px, black calc(100% - ${FADE}px), ${rightHidden ? 'transparent' : 'black'} 100%)`;

    const arrow = 'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white backdrop-blur-md transition hover:bg-primary [@media(pointer:fine)]:opacity-0 [@media(pointer:fine)]:group-hover:opacity-100';

    return (
        <div dir={dir} className="group relative flex items-center">
            <div
                ref={containerRef}
                className={`flex-1 flex overflow-x-auto overscroll-x-contain items-center scroll-smooth snap-x snap-proximity [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
                style={{ WebkitMaskImage: mask, maskImage: mask }}
            >
                {children}
            </div>

            {canStart && (
                <button type="button" onClick={() => scrollToItem(false)} aria-label="Previous" className={`${arrow} start-0`}>
                    <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </button>
            )}
            {canEnd && (
                <button type="button" onClick={() => scrollToItem(true)} aria-label="Next" className={`${arrow} end-0`}>
                    <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </button>
            )}
        </div>
    );
};

export default ContainerHorizontal;
